import React, { useState, useEffect } from 'react';
import { FaUserSlash, FaUserCheck, FaEye, FaTrash } from 'react-icons/fa'; // Добавили FaTrash, убрали FaUserShield
import { useAuth } from '../context/AuthContext';
import './UserManagement.css';

const UserManagement = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    adminUsers: 0,
    blockedUsers: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPayments, setUserPayments] = useState([]);
  const [userPaymentsLoading, setUserPaymentsLoading] = useState(false);
  const [userPaymentsError, setUserPaymentsError] = useState(null);
  const [showUserDetails, setShowUserDetails] = useState(false); // Новое состояние для отображения деталей

  // Загружаем список пользователей и статистику
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        
        const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
        const response = await fetch('/api/users', {
          headers: {
            'X-Telegram-ID': telegramId
          }
        });
        
        if (!response.ok) {
          throw new Error('Ошибка при получении списка пользователей');
        }
        
        const data = await response.json();
        if (data.users && Array.isArray(data.users)) {
          setUsers(data.users);
        }
        
        if (data.stats) {
          setStats(data.stats);
        }
      } catch (error) {
        console.error('Ошибка при загрузке пользователей:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchUsers();
  }, [user]);

  // Функция для загрузки платежей пользователя
  const fetchUserPayments = async (userId) => {
    try {
      setUserPaymentsLoading(true);
      setUserPaymentsError(null);
      
      const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
      const response = await fetch(`/api/users/${userId}/payments`, {
        headers: {
          'X-Telegram-ID': telegramId
        }
      });
      
      if (!response.ok) {
        throw new Error('Ошибка при получении платежей пользователя');
      }
      
      const data = await response.json();
      setUserPayments(data.payments || []);
    } catch (error) {
      console.error('Ошибка при загрузке платежей пользователя:', error);
      setUserPaymentsError(error.message);
    } finally {
      setUserPaymentsLoading(false);
    }
  };

  // Функция для блокировки/разблокировки пользователя
  const handleToggleBlockUser = async (userId, isCurrentlyBlocked) => {
    try {
      const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
      const response = await fetch(`/api/users/${userId}/toggle-block`, {
        method: 'POST',
        headers: {
          'X-Telegram-ID': telegramId
        }
      });
      
      if (!response.ok) {
        throw new Error('Ошибка при изменении статуса блокировки пользователя');
      }
      
      const data = await response.json();
      
      // Обновляем список пользователей
      setUsers(users.map(u => 
        u._id === userId ? { ...u, isBlocked: data.user.isBlocked } : u
      ));
      
      // Если пользователь был выбран, обновляем его данные
      if (selectedUser && selectedUser._id === userId) {
        setSelectedUser({ ...selectedUser, isBlocked: data.user.isBlocked });
      }
      
      // Обновляем статистику
      const newBlockCount = isCurrentlyBlocked ? stats.blockedUsers - 1 : stats.blockedUsers + 1;
      const newActiveCount = isCurrentlyBlocked ? stats.activeUsers + 1 : stats.activeUsers - 1;
      
      setStats({
        ...stats,
        blockedUsers: newBlockCount,
        activeUsers: newActiveCount
      });
      
      alert(data.message);
    } catch (error) {
      console.error('Ошибка при изменении статуса блокировки:', error);
      alert(error.message);
    }
  };

  // Функция для удаления пользователя
  const handleDeleteUser = async (userId) => {
    const confirmDelete = window.confirm('Вы уверены, что хотите удалить этого пользователя? Это действие необратимо и удалит все связанные с ним данные, включая покупки.');
    if (confirmDelete) {
      try {
        const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
        const response = await fetch(`/api/users/${userId}`, {
          method: 'DELETE',
          headers: {
            'X-Telegram-ID': telegramId
          }
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Ошибка при удалении пользователя');
        }

        const data = await response.json();
        alert(data.message || 'Пользователь успешно удален');

        // Обновляем список пользователей и статистику
        setUsers(users.filter(u => u._id !== userId));
        // TODO: Обновить статистику, если это необходимо и если сервер не возвращает обновленную статистику
        // Например, можно вызвать fetchUsers() снова или обновить локально, если есть точные данные
        // Для простоты пока просто фильтруем список
        
        // Закрываем детали пользователя, если удален текущий выбранный пользователь
        if (selectedUser && selectedUser._id === userId) {
          handleCloseDetails();
        }

      } catch (error) {
        console.error('Ошибка при удалении пользователя:', error);
        alert(error.message);
      }
    }
  };

  // Функция для открытия деталей пользователя
  const handleViewUser = (user) => {
    setSelectedUser(user);
    fetchUserPayments(user._id);
    setShowUserDetails(true); // Показываем детали пользователя
  };

  // Функция для закрытия деталей пользователя
  const handleCloseDetails = () => {
    setSelectedUser(null);
    setUserPayments([]);
    setShowUserDetails(false); // Скрываем детали пользователя
  };

  // Функция для форматирования даты
  const formatDate = (dateString) => {
    if (!dateString) return 'Нет данных';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Функция для форматирования денежной суммы
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Фильтрация пользователей по поисковому запросу
  const filteredUsers = users.filter(user => {
    const searchValue = searchTerm.toLowerCase();
    const fullName = `${user.firstName} ${user.lastName || ''}`.toLowerCase();
    const username = (user.username || '').toLowerCase();
    const telegramId = String(user.telegramId).toLowerCase();
    
    return fullName.includes(searchValue) || 
           username.includes(searchValue) || 
           telegramId.includes(searchValue);
  });

  // Пагинация
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  // Функция для перехода на определенную страницу
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Функция для перехода на предыдущую страницу
  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Функция для перехода на следующую страницу
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Расчет общей суммы платежей пользователя
  const calculateTotalSpent = () => {
    if (!userPayments.length) return 0;
    
    return userPayments
      .filter(payment => payment.status === 'completed')
      .reduce((total, payment) => total + payment.amount, 0);
  };

  return (
    <div className="user-management">
      {showUserDetails && selectedUser ? (
        // Отображение деталей пользователя
        <div className="user-detail-view">
          <button className="back-button" onClick={handleCloseDetails}>
            &larr; Назад к списку
          </button>
          <div className="user-info">
            <table className="user-info-table">
              <tbody>
                <tr>
                  <td>Имя:</td>
                  <td>{selectedUser.firstName} {selectedUser.lastName}</td>
                </tr>
                <tr>
                  <td>Ник в Telegram:</td>
                  <td>
                    {selectedUser.username ? (
                      <a href={`https://t.me/${selectedUser.username}`} target="_blank" rel="noopener noreferrer">
                        @{selectedUser.username}
                      </a>
                    ) : (
                      'Не указан'
                    )}
                  </td>
                </tr>
                <tr>
                  <td>Telegram ID:</td>
                  <td>{selectedUser.telegramId}</td>
                </tr>
                <tr>
                  <td>Статус:</td>
                  <td>
                    {selectedUser.isBlocked ? (
                      <span className="status-text blocked">Заблокирован</span>
                    ) : (
                      <span className="status-text active">Активен</span>
                    )}
                    {selectedUser.isAdmin && (
                      <span className="status-text admin"> (Администратор)</span>
                    )}
                  </td>
                </tr>
                <tr>
                  <td>Дата регистрации:</td>
                  <td>{formatDate(selectedUser.createdAt)}</td>
                </tr>
                <tr>
                  <td>Последняя активность:</td>
                  <td>{formatDate(selectedUser.lastActive)}</td>
                </tr>
                <tr>
                  <td>Реферал:</td>
                  <td>{selectedUser.referredBy ? (typeof selectedUser.referredBy === 'object' ? selectedUser.referredBy.name : selectedUser.referredBy) : 'Не указан'}</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="user-actions-footer">
            {selectedUser.isBlocked ? (
              <button
                className="user-action-button unblock"
                onClick={() => handleToggleBlockUser(selectedUser._id, selectedUser.isBlocked)}
              >
                <FaUserCheck /> Разблокировать
              </button>
            ) : (
              <button
                className="user-action-button block"
                onClick={() => handleToggleBlockUser(selectedUser._id, selectedUser.isBlocked)}
              >
                <FaUserSlash /> Заблокировать
              </button>
            )}
            <button
              className="user-action-button delete"
              onClick={() => handleDeleteUser(selectedUser._id)}
            >
              <FaTrash /> Удалить пользователя
            </button>
          </div>
          
          <div className="user-payments">
            <h3>История платежей</h3>
            
            {userPaymentsLoading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Загрузка платежей...</p>
              </div>
            ) : userPaymentsError ? (
              <div className="error-message">
                Ошибка при загрузке платежей: {userPaymentsError}
              </div>
            ) : (
              <>
                <p className="total-spent">
                  Всего потрачено: <strong>{formatCurrency(calculateTotalSpent())}</strong>
                </p>
                
                {userPayments.length > 0 ? (
                  <div className="payments-list">
                    {userPayments.map(payment => (
                      <div key={payment._id} className="payment-item">
                        <div className="payment-header">
                          <div className="payment-title">
                            {payment.content && payment.content.title}
                          </div>
                          <div className={`payment-status ${payment.status}`}>
                            {payment.status === 'completed' && 'Оплачен'}
                            {payment.status === 'pending' && 'В обработке'}
                            {payment.status === 'canceled' && 'Отменен'}
                            {payment.status === 'refunded' && 'Возвращен'}
                          </div>
                        </div>
                        <div className="payment-meta">
                          {formatCurrency(payment.amount)} • 
                          {payment.paymentMethod ? ` ${payment.paymentMethod} • ` : ' '}
                          {formatDate(payment.createdAt)}
                          {payment.completedAt && ` • Оплачен: ${formatDate(payment.completedAt)}`}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-payments">
                    У пользователя нет платежей
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      ) : (
        // Отображение списка пользователей
        <>
          <div className="user-stats">
            <div className="stat-card">
              <h3>ВСЕГО</h3>
          <div className="stat-value">{stats.totalUsers}</div>
        </div>
        <div className="stat-card">
          <h3>АКТИВНЫЕ</h3>
          <div className="stat-value">{stats.activeUsers}</div>
        </div>
        <div className="stat-card">
          <h3>BLOCKED</h3>
          <div className="stat-value">{stats.blockedUsers}</div>
        </div>
      </div>
      
      <div className="user-search">
        <input
          type="text"
          placeholder="Поиск по имени, нику или Telegram ID..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
      </div>
      
      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Загрузка пользователей...</p>
        </div>
      ) : (
        <>
          <div className="users-list">
            {currentUsers.length > 0 ? (
              currentUsers.map(user => (
                <div key={user._id} className="user-item">
                  <div className="user-item-header">
                    <div className="user-title">
                      <h3>{user.firstName} {user.lastName}</h3>
                      <div className="user-meta">
                        {user.username ? `@${user.username} • ` : ''}
                        ID: {user.telegramId} • 
                        Создан: {formatDate(user.createdAt)}
                      </div>
                    </div>
                    {/* Иконка статуса и старая кнопка глаза удалены */}
                    {/* Новая кнопка-иконка глаза для просмотра деталей */}
                    <button
                      className="user-action-button view-icon-main"
                      onClick={() => handleViewUser(user)}
                      title="Детали пользователя"
                    >
                      <FaEye />
                    </button>
                  </div>
                  {/* Блок user-actions теперь пуст или может быть удален, если другие кнопки не планируются */}
                  <div className="user-actions">
                  </div>
                </div>
              ))
            ) : (
              <p className="no-users">Пользователи не найдены</p>
            )}
          </div>
          
          {totalPages > 1 && (
            <div className="pagination">
              <button onClick={prevPage} disabled={currentPage === 1}>
                &laquo; Пред.
              </button>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(page => Math.abs(page - currentPage) < 2 || page === 1 || page === totalPages)
                .map((page, index, array) => {
                  // Добавляем многоточие если есть разрывы в последовательности
                  if (index > 0 && page - array[index - 1] > 1) {
                    return (
                      <React.Fragment key={`ellipsis-${page}`}>
                        <button disabled className="pagination-ellipsis">...</button>
                        <button 
                          className={currentPage === page ? 'active' : ''}
                          onClick={() => paginate(page)}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    );
                  }
                  
                  return (
                    <button
                      key={page}
                      className={currentPage === page ? 'active' : ''}
                      onClick={() => paginate(page)}
                    >
                      {page}
                    </button>
                  );
                })}
              
              <button onClick={nextPage} disabled={currentPage === totalPages}>
                След. &raquo;
              </button>
            </div>
          )}
        </>
      )}
      
        </>
      )}
    </div>
  );
};

export default UserManagement;
